import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePblClock } from '@/hooks/usePblClock';
import { isPblCaseClosureDue } from './closureDue';
import { isPblEarlyCloseStatus, isAssignedPblCaseLawyer } from './caseProgressState';

import {
  cancelPblCase,
  createPblDocument,
  createPblDocumentSignatureRequest,
  createPblLawyerExitRequest,
  getPblCase,
  getPblCaseAuthorizationFormDownload,
  getPblCaseLawyerCv,
  getPblCasePersonalDetailsCaseDownload,
  getPblDocument,
  joinPblMeeting,
  reassignPblCaseLawyer,
  selectPblAppointmentProposal,
  transitionPblCase,
  updatePblAppointmentProposal,
  updatePblMeeting,
  uploadPblDocumentFile,
} from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import {
  AppointmentProposalPanel,
  CaseDetailTitleBar,
  CaseApplicationPanel,
  CaseInfoPanel,
  ContractNegotiationPanel,
  DetailShell,
  getDocumentFilePath,
  getLawyerSummaryDisplayName,
  LawyerReviewSummaryModal,
  LawyerSummaryPanel,
  MeetingPanel,
  NoticeBar,
  InfoIcon,
  ActionRow,
  DocumentsToSignPanel,
  AppointmentProposalEditModal,
  CancelCaseConfirmModal,
  CloseCaseConfirmModal,
  DocumentDetailModal,
  LawyerExitRequestModal,
  MeetingEditModal,
  RecordResultModal,
  ReassignLawyerModal,
  type CancelCaseInput,
  type LawyerExitRequestInput,
  type ReassignLawyerInput,
  UnavailableActionModal,
} from '@/components/Pbl/CaseProgressDetail';
import { Button } from '@/components/UI/Button';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { useAppSelector } from '@/redux/store.hook';
import PATH from '@/router/path';
import type {
  PblAppointmentProposal,
  PblAppointmentProposalSelectPayload,
  PblAppointmentProposalSelectResponse,
  PblAppointmentProposalUpdatePayload,
  PblCase,
  PblDocument,
  PblLawyerCvSummary,
  PblMeeting,
  PblMeetingUpdatePayload,
} from '@/types/pbl';
import { getUserPblRoles, isPblAdmin, isPblCitizen, isPblLawyer, isPblOcAdminOrStaff } from '@/utils/pblRole';
import { errorHandler, successHandler } from '@/utils/toastHandler';
import { openStorageDownload } from '@/utils/storageDownload';

import { getUploadedPblFileId } from '@/components/Pbl/VolunteerRegistration/uploadedDocument';
import { mapPblCaseToOcManagedCase } from '../OcManageCases/caseReviewState';
import { useTranslation } from 'react-i18next';
import { extensionValidator } from '@/utils/extensionValidator';
import { fileSizeValidator } from '@/utils/fileSizeValidator';
import { validateFilename } from '@/utils/filenameValidator';
import {
  buildPblDocumentCreatePayload,
  buildPblDocumentSignatureRequestPayload,
  canClosePblCase,
  isPblReadOnlyCaseStatus,
} from './caseProgressState';
import { convertLink } from '@/utils/convert';

type PblCaseProgressDetailProps = {
  caseData: PblCase;
};

const buildLawyerMeetingCalendarPath = (caseId: string | number) =>
  PATH.pbl.lawyerMeetingCalendar.replace(':caseId', String(caseId));

const buildLawyerCaseFeedbackPath = (caseId: string | number) =>
  PATH.pbl.lawyerCaseFeedback.replace(':caseId', String(caseId));

const buildCaseDetailPath = (caseId: string | number) => PATH.pbl.caseDetail.replace(':caseId', String(caseId));

const buildAppointmentConfirmationPath = (caseId: string | number) =>
  PATH.pbl.appointmentConfirmation.replace(':caseId', String(caseId));

const getBackPath = (userData: unknown) => {
  if (isPblLawyer(userData)) return PATH.pbl.lawyerCases;
  if (isPblCitizen(userData)) return PATH.pbl.citizenCases;
  return PATH.pbl.cases;
};

const isCaseReadyForCitizenRating = (status: PblCase['status']) => status === 'closed';

const lawyerExitReasonCodeLabelKeys: Record<LawyerExitRequestInput['reasonCode'], string> = {
  scheduling_unavailable: 'pbl.caseDetailDocuments.modals.lawyerExitRequest.reasonCodes.schedulingUnavailable',
  outside_expertise: 'pbl.caseDetailDocuments.modals.lawyerExitRequest.reasonCodes.outsideExpertise',
  capacity_or_conflict: 'pbl.caseDetailDocuments.modals.lawyerExitRequest.reasonCodes.capacityOrConflict',
  other: 'pbl.caseDetailDocuments.modals.lawyerExitRequest.reasonCodes.other',
};

const openExternalUrl = (url: string | null | undefined, fallbackMessage: string) => {
  if (!url) {
    errorHandler(fallbackMessage);
    return false;
  }

  window.open(convertLink(url), '_blank', 'noopener,noreferrer');
  return true;
};

const openDownloadedBlob = (blob: Blob, fallbackMessage: string) => {
  if (!blob) {
    errorHandler(fallbackMessage);
    return false;
  }

  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    errorHandler(fallbackMessage);
    return false;
  }

  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
};

const openMeetingJoinUrl = (url: string | null | undefined, fallbackMessage: string) => {
  if (!url) {
    errorHandler(fallbackMessage);
    return false;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
};

const getNumericUserId = (userData: any) => {
  const userId = Number(userData?.id);

  return Number.isFinite(userId) ? userId : undefined;
};

type PblUploadRole = 'pbl_lawyer' | 'oc_admin' | 'oc_staff' | 'citizen';

const getPblUploadRole = (userData: any): PblUploadRole => {
  const roles = getUserPblRoles(userData);

  if (roles.includes('pbl_lawyer')) return 'pbl_lawyer';
  if (roles.includes('oc_admin')) return 'oc_admin';
  if (roles.includes('oc_staff')) return 'oc_staff';
  return 'citizen';
};

const PblCaseProgressDetail = ({ caseData }: PblCaseProgressDetailProps) => {
  //   @ts-ignore
  const { t, i18n } = useTranslation();

  const [files, setFiles] = useState<File[]>([]);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const uploadFilesRef = useRef<File[] | null>(null);

  const navigate = useNavigate();
  const auth = useAppSelector(getAuthSelector);
  const userData = auth.userData;
  const isLawyer = isPblLawyer(userData);
  const isCitizen = isPblCitizen(userData);
  const isOcUser = isPblOcAdminOrStaff(userData);
  const isAdmin = isPblAdmin(userData);
  const canManageLifecycle = isOcUser || isAdmin;
  const canManageCase = isLawyer || isCitizen || isOcUser || isAdmin;
  const canRespondToAppointmentProposal = isCitizen;
  const [caseDetail, setCaseDetail] = useState(caseData);
  const isReadOnlyCase = isPblReadOnlyCaseStatus(caseDetail.status);
  const isArchivedCase = caseDetail.status === 'case_cancelled';
  const isArchivedWorkspaceLimited = isArchivedCase && caseDetail.archive_detail_access !== 'full';
  const isOcCheckingCase = caseDetail.status === 'oc_checking';
  const canUseCaseActions = canManageCase && !isReadOnlyCase;
  const canUseMeetingActions = canUseCaseActions;
  const canEditAppointmentProposal = isOcUser && canUseCaseActions;
  const canEditMeeting = (isOcUser || isAdmin) && canUseMeetingActions;
  const canDownloadDocuments = canManageCase;
  const [joiningMeetingId, setJoiningMeetingId] = useState<number | null>(null);
  const [loadingDocumentId, setLoadingDocumentId] = useState<number | null>(null);
  const [signingDocumentId, setSigningDocumentId] = useState<number | null>(null);
  const [isClosingCase, setIsClosingCase] = useState(false);
  const [isCancellingCase, setIsCancellingCase] = useState(false);
  const [isReassigningLawyer, setIsReassigningLawyer] = useState(false);
  const [isSubmittingLawyerExitRequest, setIsSubmittingLawyerExitRequest] = useState(false);
  const [showCloseCaseConfirm, setShowCloseCaseConfirm] = useState(false);
  const [showCancelCaseConfirm, setShowCancelCaseConfirm] = useState(false);
  const [showReassignLawyer, setShowReassignLawyer] = useState(false);
  const [showLawyerExitRequest, setShowLawyerExitRequest] = useState(false);
  const [downloadingCaseApplication, setDownloadingCaseApplication] = useState<'authorization' | 'case' | null>(null);
  const [showDocumentDetail, setShowDocumentDetail] = useState(false);
  const [documentDetail, setDocumentDetail] = useState<PblDocument | null>(null);
  const [showLawyerCv, setShowLawyerCv] = useState(false);
  const [lawyerCv, setLawyerCv] = useState<PblLawyerCvSummary | null>(null);
  const [isLoadingLawyerCv, setIsLoadingLawyerCv] = useState(false);
  const [showRecordResult, setShowRecordResult] = useState(false);
  const [recordResult, setRecordResult] = useState({ success: true, message: '' });
  const [unavailableAction, setUnavailableAction] = useState<{ title: string; message: string } | null>(null);
  const [appointmentProposalActionKey, setAppointmentProposalActionKey] = useState<string | null>(null);
  const [editingAppointmentProposal, setEditingAppointmentProposal] = useState<PblAppointmentProposal | null>(null);
  const [isUpdatingAppointmentProposal, setIsUpdatingAppointmentProposal] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<PblMeeting | null>(null);
  const [isUpdatingMeeting, setIsUpdatingMeeting] = useState(false);
  const item = useMemo(() => mapPblCaseToOcManagedCase(caseDetail), [caseDetail]);
  const meetings = useMemo(
    () =>
      [...(caseDetail.meetings || [])].sort((left, right) => {
        const leftTime = new Date((left.created_at || '').replace(' ', 'T')).getTime();
        const rightTime = new Date((right.created_at || '').replace(' ', 'T')).getTime();

        return (Number.isFinite(rightTime) ? rightTime : 0) - (Number.isFinite(leftTime) ? leftTime : 0);
      }),
    [caseDetail.meetings]
  );
  const latestAppointmentProposal = useMemo(
    () => (caseDetail.appointment_proposals || [])[0] || null,
    [caseDetail.appointment_proposals]
  );
  const pendingAppointmentProposal = useMemo(
    () =>
      latestAppointmentProposal?.status === 'pending_citizen_selection' ? latestAppointmentProposal : null,
    [latestAppointmentProposal]
  );
  const unavailableAppointmentProposal = useMemo(
    () => (latestAppointmentProposal?.status === 'citizen_unavailable' ? latestAppointmentProposal : null),
    [latestAppointmentProposal]
  );
  const documents = useMemo(() => caseDetail.documents || [], [caseDetail.documents]);
  const contractDocuments = documents;
  const signDocuments = useMemo(() => documents.filter((document) => document.requires_signature), [documents]);
  const activeAssignedLawyer = useMemo(
    () =>
      caseDetail.assignments?.find(
        (assignment) => assignment.status === 'accepted' && assignment.lawyer_profile
      ) ||
      caseDetail.assignments?.find(
        (assignment) => assignment.status === 'invited' && assignment.lawyer_profile
      ),
    [caseDetail.assignments]
  );
  const assignedLawyerName = useMemo(
    () =>
      getLawyerSummaryDisplayName(caseDetail.lawyer_summary) ||
      activeAssignedLawyer?.lawyer_profile?.full_name_en ||
      activeAssignedLawyer?.lawyer_profile?.full_name_zh ||
      'Assigned lawyer',
    [activeAssignedLawyer, caseDetail.lawyer_summary]
  );
  const canCloseCase = useMemo(() => canClosePblCase(caseDetail, userData), [caseDetail, userData]);
  const now = usePblClock();
  const closureDue = isPblCaseClosureDue(caseDetail, userData, now);
  const canReassignCaseStatus = [
    'lawyer_accepted',
    'oc_checking',
    'pending_lawyer_timeslot',
    'pending_appointment_confirmation',
    'meeting_scheduled',
    'in_progress',
    'pending_documents',
    'pending_completion',
    'pending_evaluation',
  ].includes(caseDetail.status);
  const activeAssignmentId = useMemo(
    () => caseDetail.assignments?.find((assignment) => assignment.status === 'accepted')?.id,
    [caseDetail.assignments]
  );
  const canReassignCurrentLawyer = Boolean(canManageLifecycle && canReassignCaseStatus && activeAssignmentId);
  const pendingLawyerExitRequests = useMemo(
    () => (caseDetail.lawyer_exit_requests || []).filter((request) => request.status === 'pending'),
    [caseDetail.lawyer_exit_requests]
  );
  const canUseRatingAlert =
    caseDetail.rating_alert?.show === true &&
    ((!caseDetail.rating_alert.evaluator_role && (isCitizen || isLawyer)) ||
      (caseDetail.rating_alert.evaluator_role === 'citizen' && isCitizen) ||
      (caseDetail.rating_alert.evaluator_role === 'pbl_lawyer' && isLawyer));
  const shouldShowRatingAlert = isCaseReadyForCitizenRating(caseDetail.status) && canUseRatingAlert;
  const shouldAutoRedirectToAppointmentConfirmation =
    isCitizen &&
    caseDetail.status === 'pending_appointment_confirmation' &&
    Boolean(pendingAppointmentProposal) &&
    canRespondToAppointmentProposal;

  useEffect(() => {
    if (!shouldAutoRedirectToAppointmentConfirmation) return;

    navigate(buildAppointmentConfirmationPath(caseDetail.id), { replace: true });
  }, [caseDetail.id, navigate, shouldAutoRedirectToAppointmentConfirmation]);

  const canCreateMeeting =
    isLawyer &&
    !isReadOnlyCase &&
    [
      'pending_lawyer_timeslot',
      'meeting_scheduled',
      'in_progress',
      'pending_documents',
      'pending_completion',
      'pending_evaluation',
    ].includes(caseDetail.status);
  const unavailableReason =
    unavailableAppointmentProposal?.citizen_response_text?.trim() ||
    t('pbl.caseDetailDocuments.page.unavailableNoticeReported');
  const casePartyActionError = t('pbl.caseDetailDocuments.messages.casePartyActionDenied');
  const closeCaseActionError = t('pbl.caseDetailDocuments.messages.closeCaseDenied');
  const lifecycleActionError = t('pbl.caseDetailDocuments.messages.caseLifecycleActionDenied');
  const lawyerExitRequestError = t('pbl.caseDetailDocuments.messages.requestReviewDenied');
  const meetingActionError = t('pbl.caseDetailDocuments.messages.meetingActionDenied');
  const appointmentProposalActionError = t('pbl.caseDetailDocuments.messages.appointmentProposalActionDenied');
  const readOnlyCaseActionError = t('pbl.caseDetailDocuments.messages.readOnlyCase');
  const appointmentProposalUnavailableReasonError = t(
    'pbl.caseDetailDocuments.messages.unavailableReasonRequired'
  );

  useEffect(() => {
    setCaseDetail(caseData);
    setLawyerCv(null);
    setShowLawyerCv(false);
  }, [caseData]);

  const ensureCasePartyAction = () => {
    if (isReadOnlyCase) {
      errorHandler(readOnlyCaseActionError);
      return false;
    }

    if (canManageCase) return true;

    errorHandler(casePartyActionError);
    return false;
  };

  const ensureMeetingAction = () => {
    if (isReadOnlyCase) {
      errorHandler(readOnlyCaseActionError);
      return false;
    }

    if (canUseMeetingActions) return true;

    errorHandler(meetingActionError);
    return false;
  };

  const ensureAppointmentProposalAction = useCallback(() => {
    if (isReadOnlyCase) {
      errorHandler(readOnlyCaseActionError);
      return false;
    }

    if (canRespondToAppointmentProposal) return true;

    errorHandler(appointmentProposalActionError);
    return false;
  }, [appointmentProposalActionError, canRespondToAppointmentProposal, isReadOnlyCase, readOnlyCaseActionError]);

  const ensureCloseCaseAction = () => {
    if (isReadOnlyCase) {
      errorHandler(readOnlyCaseActionError);
      return false;
    }

    if (canCloseCase) return true;

    errorHandler(closeCaseActionError);
    return false;
  };

  const ensureLifecycleAction = () => {
    if (isReadOnlyCase) {
      errorHandler(readOnlyCaseActionError);
      return false;
    }

    if (canManageLifecycle) return true;

    errorHandler(lifecycleActionError);
    return false;
  };

  const ensureLawyerExitRequestAction = () => {
    if (isReadOnlyCase) {
      errorHandler(readOnlyCaseActionError);
      return false;
    }

    if (isLawyer) return true;

    errorHandler(lawyerExitRequestError);
    return false;
  };

  const goToRating = () => {
    if (!shouldShowRatingAlert) return;

    if (isLawyer) {
      navigate(buildLawyerCaseFeedbackPath(caseDetail.id));
      return;
    }

    if (isCitizen) navigate(`${buildCaseDetailPath(caseDetail.id)}?status=to_be_rated`);
  };

  const closeCase = async (reason?: string) => {
    if (!ensureCloseCaseAction() || isClosingCase) return false;

    setIsClosingCase(true);

    try {
      const response = await transitionPblCase(caseDetail.id, {
        to_status: 'closed',
        reason: isPblEarlyCloseStatus(caseDetail.status) ? reason?.trim() : t('pbl.caseDetailDocuments.messages.completionStatus'),
        ...(isPblEarlyCloseStatus(caseDetail.status) ? { close_without_consultation: true } : {}),
      });

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToCloseTheCase'));
        return false;
      }

      let nextCase = response.data.data;

      try {
        const detailResponse = await getPblCase(caseDetail.id);

        if (detailResponse.data.success !== false) {
          nextCase = detailResponse.data.data;
        }
      } catch {
        nextCase = response.data.data;
      }

      setCaseDetail(nextCase);
      successHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.caseClosed'));
      return true;
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToCloseTheCase'));
      return false;
    } finally {
      setIsClosingCase(false);
    }
  };

  const openCloseCaseConfirm = () => {
    if (ensureCloseCaseAction()) setShowCloseCaseConfirm(true);
  };

  const confirmCloseCase = async (reason?: string) => {
    const closed = await closeCase(reason);
    if (closed) setShowCloseCaseConfirm(false);
  };

  const cancelCase = async ({ reason }: CancelCaseInput) => {
    if (!ensureLifecycleAction() || isCancellingCase) return false;

    setIsCancellingCase(true);

    try {
      const response = await cancelPblCase(caseDetail.id, {
        reason,
      });

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToCancelTheCase'));
        return false;
      }

      setCaseDetail(response.data.data);
      successHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.caseCancelled'));
      return true;
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToCancelTheCase'));
      return false;
    } finally {
      setIsCancellingCase(false);
    }
  };

  const confirmCancelCase = async (data: CancelCaseInput) => {
    const cancelled = await cancelCase(data);
    if (cancelled) setShowCancelCaseConfirm(false);
  };

  const reassignLawyer = async ({ reason }: ReassignLawyerInput) => {
    if (!ensureLifecycleAction() || isReassigningLawyer) return false;
    if (!activeAssignmentId) {
      errorHandler(t('pbl.pagesHardcoded.caseProgressDetail.noAcceptedLawyerAssignmentIsAvailableForReassignment'));
      return false;
    }

    setIsReassigningLawyer(true);

    try {
      const response = await reassignPblCaseLawyer(caseDetail.id, {
        reason,
        assignment_id: activeAssignmentId,
      });

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToReassignLawyer'));
        return false;
      }

      successHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.caseReassigned'));
      if (canManageLifecycle) {
        navigate(PATH.pbl.cases, { replace: true });
      } else {
        setCaseDetail(response.data.data.case);
      }
      return true;
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToReassignLawyer'));
      return false;
    } finally {
      setIsReassigningLawyer(false);
    }
  };

  const confirmReassignLawyer = async (data: ReassignLawyerInput) => {
    const reassigned = await reassignLawyer(data);
    if (reassigned) setShowReassignLawyer(false);
  };

  const submitLawyerExitRequest = async ({ reasonCode, reason }: LawyerExitRequestInput) => {
    if (!ensureLawyerExitRequestAction() || isSubmittingLawyerExitRequest) return false;

    setIsSubmittingLawyerExitRequest(true);

    try {
      const response = await createPblLawyerExitRequest(caseDetail.id, {
        reason_code: reasonCode,
        reason,
      });

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToSubmitTheReviewRequest'));
        return false;
      }

      try {
        const detailResponse = await getPblCase(caseDetail.id);

        if (detailResponse.data.success !== false) {
          setCaseDetail(detailResponse.data.data);
        } else {
          setCaseDetail((currentCase) => ({
            ...currentCase,
            status: 'oc_checking',
            lawyer_exit_requests: [response.data.data, ...(currentCase.lawyer_exit_requests || [])],
          }));
        }
      } catch {
        setCaseDetail((currentCase) => ({
          ...currentCase,
          status: 'oc_checking',
          lawyer_exit_requests: [response.data.data, ...(currentCase.lawyer_exit_requests || [])],
        }));
      }
      successHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.requestSubmitted'));
      return true;
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToSubmitTheReviewRequest')
      );
      return false;
    } finally {
      setIsSubmittingLawyerExitRequest(false);
    }
  };

  const confirmLawyerExitRequest = async (data: LawyerExitRequestInput) => {
    const submitted = await submitLawyerExitRequest(data);
    if (submitted) setShowLawyerExitRequest(false);
  };

  const downloadAuthorizationForm = async () => {
    if (downloadingCaseApplication) return;

    setDownloadingCaseApplication('authorization');

    try {
      const response = await getPblCaseAuthorizationFormDownload(caseDetail.id);
      openDownloadedBlob(response.data, 'Failed to download authorization form.');
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToDownloadAuthorizationForm')
      );
    } finally {
      setDownloadingCaseApplication(null);
    }
  };

  const downloadPersonalDetailsCase = async () => {
    if (downloadingCaseApplication) return;

    setDownloadingCaseApplication('case');

    try {
      const response = await getPblCasePersonalDetailsCaseDownload(caseDetail.id);
      openDownloadedBlob(response.data, t('pbl.pagesHardcoded.caseProgressDetail.failedToDownloadCaseApplication'));
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToDownloadCaseApplication'));
    } finally {
      setDownloadingCaseApplication(null);
    }
  };

  const joinMeeting = async (meeting: PblMeeting) => {
    if (!ensureMeetingAction()) return;

    setJoiningMeetingId(meeting.id);

    try {
      const response = await joinPblMeeting(meeting.join_endpoint || meeting.id, i18n.language);

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToJoinTheMeeting'));
        return;
      }

      openMeetingJoinUrl(
        response.data.data.join_url,
        t('pbl.pagesHardcoded.caseProgressDetail.noMeetingLinkIsAvailable'),
      );
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToJoinTheMeeting'));
    } finally {
      setJoiningMeetingId(null);
    }
  };

  const buildNextCaseAfterAppointmentProposalResponse = useCallback(
    (currentCase: PblCase, responseData: PblAppointmentProposalSelectResponse): PblCase => {
      const responseCase = responseData.case;
      const responseMeeting = responseData.meeting;

      if (responseCase) {
        if (
          responseMeeting &&
          !(responseCase.meetings || []).some((meeting) => meeting.id === responseMeeting.id)
        ) {
          return {
            ...responseCase,
            meetings: [responseMeeting, ...(responseCase.meetings || [])],
          };
        }

        return responseCase;
      }

      return {
        ...currentCase,
        appointment_proposals: (currentCase.appointment_proposals || []).map((proposal) =>
          proposal.id === responseData.proposal.id ? responseData.proposal : proposal
        ),
        ...(responseMeeting
          ? {
              status: 'meeting_scheduled',
              meetings: [responseMeeting, ...(currentCase.meetings || [])],
            }
          : {}),
      };
    },
    []
  );

  const respondToAppointmentProposal = useCallback(
    async (
      proposal: PblAppointmentProposal,
      payload: PblAppointmentProposalSelectPayload,
      actionKey: string,
      fallbackSuccessMessage: string
    ) => {
      if (!ensureAppointmentProposalAction() || appointmentProposalActionKey) return;

      setAppointmentProposalActionKey(actionKey);

      try {
        const response = await selectPblAppointmentProposal(proposal.id, payload);

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToRespondToTheAppointmentProposal'));
          return;
        }

        setCaseDetail((currentCase) =>
          buildNextCaseAfterAppointmentProposalResponse(currentCase, response.data.data)
        );
        successHandler(response.data.message || fallbackSuccessMessage);
      } catch (error: any) {
        errorHandler(
          error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToRespondToTheAppointmentProposal')
        );
      } finally {
        setAppointmentProposalActionKey(null);
      }
    },
    [appointmentProposalActionKey, buildNextCaseAfterAppointmentProposalResponse, ensureAppointmentProposalAction]
  );

  const selectAppointmentProposalOption = useCallback(
    (proposal: PblAppointmentProposal, optionIndex: number) =>
      respondToAppointmentProposal(
        proposal,
        { selected_option_index: optionIndex },
        `select-${proposal.id}-${optionIndex}`,
        t('pbl.caseDetailDocuments.messages.meetingTimeSelected')
      ),
    [respondToAppointmentProposal, t]
  );

  const declineAppointmentProposalOptions = useCallback(
    (proposal: PblAppointmentProposal, unavailableReason: string) => {
      const trimmedUnavailableReason = unavailableReason.trim();

      if (!trimmedUnavailableReason) {
        errorHandler(appointmentProposalUnavailableReasonError);
        return;
      }

      respondToAppointmentProposal(
        proposal,
        { unavailable_reason: trimmedUnavailableReason },
        `decline-${proposal.id}`,
        t('pbl.caseDetailDocuments.messages.proposedMeetingTimesDeclined')
      );
    },
    [appointmentProposalUnavailableReasonError, respondToAppointmentProposal, t]
  );

  const inspectDocument = async (document: PblDocument) => {
    setLoadingDocumentId(document.id);
    setDocumentDetail(document);
    setShowDocumentDetail(true);

    try {
      const response = await getPblDocument(document.id);

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToLoadDocumentDetail'));
        return;
      }

      setDocumentDetail(response.data.data);
    } catch (error: any) {
      if (error.response?.status === 403) {
        errorHandler(t('pbl.pagesHardcoded.caseProgressDetail.noPermissionToViewThisDocument'));
        setShowDocumentDetail(false);
        return;
      }

      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToLoadDocumentDetail'));
    } finally {
      setLoadingDocumentId(null);
    }
  };

  const downloadDocument = (document: PblDocument) => {
    const path = getDocumentFilePath(document);
    if (!path) {
      errorHandler('No file is available for download.');
      return;
    }
    void openStorageDownload(path);
  };

  const inviteDocumentToSign = async (document: PblDocument) => {
    if (!ensureCasePartyAction()) return;

    if (!document.storage_file_id && !document.storage_file?.id) {
      errorHandler(t('pbl.pagesHardcoded.caseProgressDetail.onlyUploadedDocumentsCanBeInvitedForSignature'));
      return;
    }

    const currentUserId = getNumericUserId(userData);
    const payload = buildPblDocumentSignatureRequestPayload({
      caseData: caseDetail,
      signerUserIds: currentUserId ? [currentUserId] : [],
      includeAssignedLawyer: true,
      metadata: {
        source: 'contract_negotiation',
        pbl_case_id: caseDetail.id,
      },
    });

    if (payload.signer_user_ids.length === 0) {
      errorHandler(t('pbl.pagesHardcoded.caseProgressDetail.noEligibleSignerIsAvailableForThisDocument'));
      return;
    }

    setSigningDocumentId(document.id);

    try {
      const response = await createPblDocumentSignatureRequest(document.id, payload);

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToCreateSignatureRequest'));
        return;
      }

      const updatedDocument = response.data.data;

      setCaseDetail((prevCase) => ({
        ...prevCase,
        documents: (prevCase.documents || []).map((item) => (item.id === document.id ? updatedDocument : item)),
      }));
      successHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.signatureRequestCreated'));
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToCreateSignatureRequest')
      );
    } finally {
      setSigningDocumentId(null);
    }
  };

  const onDragStateChange = useCallback((_dragActive: boolean) => {}, []);

  const onFilesDrop = useCallback((files: File[]) => {
    setFiles(files);
  }, []);

  const uploadContractFiles = useCallback(
    async (selectedFiles: File[]) => {
      if (selectedFiles.length === 0) return;
      if (isReadOnlyCase) {
        errorHandler(readOnlyCaseActionError);
        setFiles([]);
        return;
      }

      if (!canManageCase) {
        errorHandler(casePartyActionError);
        setFiles([]);
        return;
      }

      setIsUploadingDocument(true);

      try {
        if (!extensionValidator(selectedFiles)) {
          errorHandler(t('error.extension'));
          return;
        }

        if (!validateFilename(selectedFiles)) {
          errorHandler(t('error.invalidFileName'));
          return;
        }

        if (selectedFiles.some((file) => !fileSizeValidator(file, 15))) {
          errorHandler(t('error.fileLimit'));
          return;
        }

        const currentUserId = getNumericUserId(userData);
        const uploadedByRole = getPblUploadRole(userData);
        const createdDocuments: PblDocument[] = [];

        for (const file of selectedFiles) {
          const uploadResponse = await uploadPblDocumentFile(file);
          const storageFileId = getUploadedPblFileId(uploadResponse.data.data);

          if (!storageFileId) {
            errorHandler(uploadResponse.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToUploadDocument'));
            return;
          }

          const documentResponse = await createPblDocument(
            caseDetail.id,
            buildPblDocumentCreatePayload({
              caseData: caseDetail,
              storageFileId,
              title: file.name,
              documentType: 'signed_document',
              participantUserIds: currentUserId ? [currentUserId] : [],
              includeAssignedLawyer: true,
              metadata: {
                uploaded_by_role: uploadedByRole,
                ...(currentUserId ? { uploaded_by_user_id: currentUserId } : {}),
              },
            })
          );

          if (documentResponse.data.success === false) {
            errorHandler(documentResponse.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToCreateDocument'));
            return;
          }

          createdDocuments.push(documentResponse.data.data);
        }

        if (createdDocuments.length > 0) {
          const appendCreatedDocuments = () => {
            setCaseDetail((prevCase) => ({
              ...prevCase,
              documents: [...(prevCase.documents || []), ...createdDocuments],
            }));
          };

          try {
            const caseResponse = await getPblCase(caseDetail.id);

            if (caseResponse.data.success === false) {
              appendCreatedDocuments();
            } else {
              setCaseDetail(caseResponse.data.data);
            }
          } catch {
            appendCreatedDocuments();
          }

          successHandler(createdDocuments.length === 1 ? t('pbl.pagesHardcoded.caseProgressDetail.documentUploaded') : t('pbl.pagesHardcoded.caseProgressDetail.documentsUploaded'));
        }
      } catch (error: any) {
        errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToUploadDocument'));
      } finally {
        setIsUploadingDocument(false);
        setFiles([]);
      }
    },
    [canManageCase, caseDetail, casePartyActionError, isReadOnlyCase, readOnlyCaseActionError, t, userData]
  );

  useEffect(() => {
    if (files.length > 0 && uploadFilesRef.current !== files) {
      uploadFilesRef.current = files;
      uploadContractFiles(files);
      return;
    }

    if (files.length === 0) uploadFilesRef.current = null;
  }, [files, uploadContractFiles]);

  const updateAppointmentProposalTimes = useCallback(
    async (payload: PblAppointmentProposalUpdatePayload) => {
      if (!editingAppointmentProposal || !canEditAppointmentProposal || isUpdatingAppointmentProposal) return;

      setIsUpdatingAppointmentProposal(true);

      try {
        const response = await updatePblAppointmentProposal(editingAppointmentProposal.id, payload);

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToUpdateProposedTimes'));
          return;
        }

        const detailResponse = await getPblCase(caseDetail.id);
        if (detailResponse.data.success === false) {
          errorHandler(detailResponse.data.message || t('error.server'));
          return;
        }
        setCaseDetail(detailResponse.data.data);
        setEditingAppointmentProposal(null);
        successHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.proposedTimesUpdated'));
      } catch (error: any) {
        errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToUpdateProposedTimes'));
      } finally {
        setIsUpdatingAppointmentProposal(false);
      }
    },
    [canEditAppointmentProposal, editingAppointmentProposal, isUpdatingAppointmentProposal, caseDetail.id]
  );

  const updateMeetingTime = useCallback(
    async (payload: PblMeetingUpdatePayload) => {
      if (!editingMeeting || !canEditMeeting || isUpdatingMeeting) return;

      setIsUpdatingMeeting(true);

      try {
        const response = await updatePblMeeting(editingMeeting.id, payload);

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToUpdateMeetingTime'));
          return;
        }

        setCaseDetail((prevCase) => ({
          ...prevCase,
          meetings: (prevCase.meetings || []).map((meeting) =>
            meeting.id === response.data.data.id ? response.data.data : meeting
          ),
        }));
        setEditingMeeting(null);
        successHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.meetingTimeUpdated'));
      } catch (error: any) {
        errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToUpdateMeetingTime'));
      } finally {
        setIsUpdatingMeeting(false);
      }
    },
    [canEditMeeting, editingMeeting, isUpdatingMeeting]
  );

  const showUnavailableAction = useCallback((title: string, message: string) => {
    setUnavailableAction({ title, message });
  }, []);

  const openLawyerCv = useCallback(async () => {
    if (!caseDetail.lawyer_summary || isLoadingLawyerCv) return;

    setShowLawyerCv(true);
    if (lawyerCv) return;

    setIsLoadingLawyerCv(true);

    try {
      const response = await getPblCaseLawyerCv(caseDetail.id);

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToLoadLawyerCv'));
        return;
      }

      setLawyerCv(response.data.data);
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToLoadLawyerCv'));
    } finally {
      setIsLoadingLawyerCv(false);
    }
  }, [caseDetail.id, caseDetail.lawyer_summary, isLoadingLawyerCv, lawyerCv]);

  return (
    <PageContainer
      title={
        <CaseDetailTitleBar>
          <Button
            type="button"
            size="md"
            variant="primary"
            rounded={false}
            onClick={() => navigate(getBackPath(userData))}
          >
            <span className="case-detail-back-label">{t('pbl.pagesHardcoded.caseProgressDetail.back')}</span>
            <img className="case-detail-back-icon" src="/images/profile/back.svg" alt="" aria-hidden="true" />
          </Button>
          <span>
            {t('pbl.pagesHardcoded.caseProgressDetail.home')}<strong>{item.caseId}</strong>
          </span>
        </CaseDetailTitleBar>
      }
    >
      <DocumentDetailModal
        showModal={showDocumentDetail}
        setShowModal={setShowDocumentDetail}
        document={documentDetail}
        isLoading={Boolean(loadingDocumentId)}
        onDownloadDocument={downloadDocument}
      />
      <LawyerReviewSummaryModal
        showModal={showLawyerCv}
        setShowModal={setShowLawyerCv}
        cv={lawyerCv}
        isLoading={isLoadingLawyerCv}
      />
      <RecordResultModal
        showModal={showRecordResult}
        setShowModal={setShowRecordResult}
        success={recordResult.success}
        message={recordResult.message}
      />
      <CloseCaseConfirmModal
        showModal={showCloseCaseConfirm}
        setShowModal={setShowCloseCaseConfirm}
        isSubmitting={isClosingCase}
        onConfirm={confirmCloseCase}
        requireReason={isPblEarlyCloseStatus(caseDetail.status)}
      />
      <CancelCaseConfirmModal
        showModal={showCancelCaseConfirm}
        setShowModal={setShowCancelCaseConfirm}
        isSubmitting={isCancellingCase}
        onConfirm={confirmCancelCase}
      />
      <ReassignLawyerModal
        showModal={showReassignLawyer}
        setShowModal={setShowReassignLawyer}
        isSubmitting={isReassigningLawyer}
        onConfirm={confirmReassignLawyer}
      />
      <LawyerExitRequestModal
        showModal={showLawyerExitRequest}
        setShowModal={setShowLawyerExitRequest}
        isSubmitting={isSubmittingLawyerExitRequest}
        onConfirm={confirmLawyerExitRequest}
      />
      <UnavailableActionModal
        showModal={Boolean(unavailableAction)}
        setShowModal={(show) => {
          if (!show) setUnavailableAction(null);
        }}
        title={unavailableAction?.title || ''}
        message={unavailableAction?.message || ''}
      />
      <AppointmentProposalEditModal
        showModal={Boolean(editingAppointmentProposal)}
        setShowModal={(show) => {
          if (!show) setEditingAppointmentProposal(null);
        }}
        proposal={editingAppointmentProposal}
        isSubmitting={isUpdatingAppointmentProposal}
        onSubmit={updateAppointmentProposalTimes}
      />
      <MeetingEditModal
        showModal={Boolean(editingMeeting)}
        setShowModal={(show) => {
          if (!show) setEditingMeeting(null);
        }}
        meeting={editingMeeting}
        isSubmitting={isUpdatingMeeting}
        onSubmit={updateMeetingTime}
      />
      <DetailShell>
        {closureDue && (
          <NoticeBar role="status">
            <span>{t('pbl.backlogContinuation.closureNotice')}</span>
            <Button type="button" variant="primary" disabled={isClosingCase} onClick={openCloseCaseConfirm}>
              {t('pbl.backlogContinuation.reviewClosure')}
            </Button>
          </NoticeBar>
        )}
        {!isReadOnlyCase && (canCreateMeeting || canCloseCase || canManageLifecycle || (isLawyer && !isOcCheckingCase)) && (
          <ActionRow>
            {canCreateMeeting && (
              <Button
                type="button"
                size="md"
                variant="regular"
                rounded={false}
                onClick={() => navigate(buildLawyerMeetingCalendarPath(caseDetail.id))}
              >
                {t('pbl.pagesHardcoded.caseProgressDetail.createMeeting')}</Button>
            )}
            {canCloseCase && (
              <Button
                type="button"
                size="md"
                variant="regular"
                rounded={false}
                disabled={isClosingCase}
                onClick={openCloseCaseConfirm}
              >
                {isClosingCase ? t('pbl.pagesHardcoded.caseProgressDetail.closing') : t('pbl.pagesHardcoded.caseProgressDetail.closeTheCase')}
              </Button>
            )}
            {canManageLifecycle && (
              <>
                <Button
                  type="button"
                  size="md"
                  variant="danger"
                  rounded={false}
                  disabled={isCancellingCase}
                  onClick={() => {
                    if (ensureLifecycleAction()) setShowCancelCaseConfirm(true);
                  }}
                >
                  {t('pbl.pagesHardcoded.caseProgressDetail.cancelCase')}</Button>
                {canReassignCurrentLawyer && (
                  <Button
                    type="button"
                    size="md"
                    variant="primary-invert"
                    rounded={false}
                    disabled={isReassigningLawyer}
                    onClick={() => {
                      if (ensureLifecycleAction()) setShowReassignLawyer(true);
                    }}
                  >
                    {t('pbl.pagesHardcoded.caseProgressDetail.reassignLawyer')}</Button>
                )}
              </>
            )}
            {isLawyer && !isOcCheckingCase && pendingLawyerExitRequests.length === 0 && (
              <Button
                type="button"
                size="md"
                variant="primary-invert"
                rounded={false}
                disabled={isSubmittingLawyerExitRequest}
                onClick={() => {
                  if (ensureLawyerExitRequestAction()) setShowLawyerExitRequest(true);
                }}
              >
                {t('pbl.pagesHardcoded.caseProgressDetail.requestOcReview')}</Button>
            )}
          </ActionRow>
        )}

        {canManageLifecycle && pendingLawyerExitRequests.length > 0 && (
          <NoticeBar>
            <InfoIcon>i</InfoIcon>
            <span>
              {t('pbl.pagesHardcoded.caseProgressDetail.aLawyerHasRequestedOcReviewForThis')}{pendingLawyerExitRequests.map((request) => {
                const reasonCode = request.reason_code?.trim() as LawyerExitRequestInput['reasonCode'] | undefined;
                const reasonCodeLabel = reasonCode
                  ? (t as (key: string, options?: Record<string, unknown>) => string)(
                      lawyerExitReasonCodeLabelKeys[reasonCode] || '',
                      { defaultValue: reasonCode }
                    )
                  : '';
                const reason = request.reason?.trim();

                return reasonCodeLabel || reason ? (
                  <strong key={request.id}>
                    <br />
                    {reasonCodeLabel ? (
                      <>
                        {t('pbl.pagesHardcoded.caseProgressDetail.reasonCode')}{reasonCodeLabel}
                        <br />
                      </>
                    ) : null}
                    {reason ? <>{t('pbl.pagesHardcoded.caseProgressDetail.reason')}{reason}</> : null}
                  </strong>
                ) : null;
              })}
            </span>
          </NoticeBar>
        )}

        {!canManageLifecycle && isOcCheckingCase && (
          <NoticeBar>
            <InfoIcon>i</InfoIcon>
            <span>{t('pbl.pagesHardcoded.caseProgressDetail.thisCaseIsUnderOcReviewYouCan')}</span>
          </NoticeBar>
        )}


        {isCitizen && caseDetail.status === 'oc_review_failed' && (
          <NoticeBar>
            <InfoIcon>i</InfoIcon>
            <span>This case needs updates before it can be reviewed again.</span>
            <Button
              type="button"
              size="md"
              variant="primary"
              rounded={false}
              onClick={() => navigate(PATH.pbl.citizenCaseEdit.replace(':caseId', String(caseDetail.id)))}
            >
              Edit case
            </Button>
          </NoticeBar>
        )}

        {shouldShowRatingAlert && (
          <NoticeBar>
            <InfoIcon>i</InfoIcon>
            <span>
              {t('pbl.pagesHardcoded.caseProgressDetail.yourCaseHasBeenClosedPleaseHelpUs')}</span>
            <Button type="button" size="md" variant="primary" rounded={false} onClick={goToRating}>
              {t('pbl.pagesHardcoded.caseProgressDetail.goToRating')}</Button>
          </NoticeBar>
        )}

        {pendingAppointmentProposal && canRespondToAppointmentProposal && (
          <NoticeBar>
            <InfoIcon>i</InfoIcon>
            <span>{t('pbl.pagesHardcoded.caseProgressDetail.theLawyerHasSentAMeetingInvitationPlease')}</span>
            <Button
              type="button"
              size="md"
              variant="primary"
              rounded={false}
              onClick={() => navigate(buildAppointmentConfirmationPath(caseDetail.id))}
            >
              {t('pbl.pagesHardcoded.caseProgressDetail.goToConfirm')}</Button>
          </NoticeBar>
        )}

        {caseDetail.status === 'pending_lawyer_timeslot' && unavailableAppointmentProposal && (
          <NoticeBar>
            <InfoIcon>i</InfoIcon>
            <span>
              {isLawyer
                ? t('pbl.pagesHardcoded.caseProgressDetail.enquirerCannotParticipateInAnyOfTheMeeting')
                : t('pbl.pagesHardcoded.caseProgressDetail.theProposedMeetingTimesWereDeclinedTheLawyer')}{' '}
              <strong>{t('pbl.pagesHardcoded.caseProgressDetail.reason')}{unavailableReason}</strong>
            </span>
            {canCreateMeeting && (
              <Button
                type="button"
                size="md"
                variant="primary"
                rounded={false}
                onClick={() => navigate(buildLawyerMeetingCalendarPath(caseDetail.id))}
              >
                {t('pbl.pagesHardcoded.caseProgressDetail.createMeeting')}</Button>
            )}
          </NoticeBar>
        )}

        <CaseInfoPanel item={item} />
        {(caseDetail.urgency || caseDetail.court_deadline) && <NoticeBar>
          {t('backlogCaseUpdates.urgency')}: {caseDetail.urgency ? t(`backlogCaseUpdates.${caseDetail.urgency}`) : '—'}
          {' · '}{t('backlogCaseUpdates.courtDeadline')}: {caseDetail.court_deadline || '—'}
        </NoticeBar>}

        <LawyerSummaryPanel
          summary={caseDetail.lawyer_summary}
          isLoadingCv={isLoadingLawyerCv}
          onViewCv={openLawyerCv}
        />

        <CaseApplicationPanel
          archived={isArchivedWorkspaceLimited}
          canViewRatingHistory={!isReadOnlyCase && !isOcUser}
          isDownloadingAuthorization={downloadingCaseApplication === 'authorization'}
          isDownloadingCase={downloadingCaseApplication === 'case'}
          onDownloadAuthorizationForm={downloadAuthorizationForm}
          onDownloadPersonalDetailsCase={downloadPersonalDetailsCase}
        />

        {isLawyer && canUseCaseActions && caseDetail.status === 'pending_appointment_confirmation' && pendingAppointmentProposal
          && Number(pendingAppointmentProposal.created_by_user_id) === Number(userData?.id)
          && isAssignedPblCaseLawyer(caseDetail, userData, new Set(['accepted'])) && (
          <ActionRow><Button type="button" variant="primary" onClick={() => navigate(buildLawyerMeetingCalendarPath(caseDetail.id))}>
            {t('pbl.caseDetailDocuments.appointmentProposal.edit')}
          </Button></ActionRow>
        )}
        {isOcUser && canUseCaseActions && caseDetail.status === 'pending_appointment_confirmation' && pendingAppointmentProposal && (
          <ActionRow><Button type="button" onClick={() => navigate(PATH.pbl.ocAppointmentConfirmation.replace(':caseId', String(caseDetail.id)))}>
            {t('pbl.ocAppointmentProxy.action')}
          </Button></ActionRow>
        )}
        {!isArchivedWorkspaceLimited && pendingAppointmentProposal && !canRespondToAppointmentProposal && (
          <AppointmentProposalPanel
            proposal={pendingAppointmentProposal}
            canRespond={false}
            canEdit={canEditAppointmentProposal}
            respondingActionKey={appointmentProposalActionKey}
            onSelectOption={selectAppointmentProposalOption}
            onDeclineAll={declineAppointmentProposalOptions}
            onEditProposal={setEditingAppointmentProposal}
          />
        )}

        {!isArchivedWorkspaceLimited && (
          <>
            <MeetingPanel
              meetings={meetings}
              canManageCase={canUseMeetingActions}
              canEditMeeting={canEditMeeting}
              joiningMeetingId={joiningMeetingId}
              onJoinMeeting={joinMeeting}
              onEditMeeting={setEditingMeeting}
            />

            <ContractNegotiationPanel
              documents={contractDocuments}
              canManageCase={canUseCaseActions}
              canDownloadDocument={canDownloadDocuments}
              files={files}
              setFiles={setFiles}
              onDragStateChange={onDragStateChange}
              onFilesDrop={onFilesDrop}
              fileNamePatternText={t('pattern.fileName')}
              isUploading={isUploadingDocument}
              signingDocumentId={signingDocumentId}
              loadingDocumentId={loadingDocumentId}
              onInspectDocument={inspectDocument}
              onDownloadDocument={downloadDocument}
              onInviteToSign={inviteDocumentToSign}
            />

            {/* <DocumentsToSignPanel
              documents={signDocuments}
              item={item}
              assignedLawyerName={assignedLawyerName}
              canManageCase={canUseCaseActions}
            /> */}
          </>
        )}

      </DetailShell>
    </PageContainer>
  );
};

export default PblCaseProgressDetail;
